// Insights, as the app holds it: the chosen range, the placed bets with their
// frozen tickets beside them, and the paper trail folded per strategy key.
//
// ONE-WAY IMPORT, as everywhere in this folder. The arithmetic is all in
// `lib/insights.js`, which is pure and tested; what is left here is the
// reactivity, the clock and the two joins that need the store — a bet to the
// frozen multi that printed it (`findTicket`), and a strategy id to the name
// the pad would give it TODAY (`findAny`).
//
// THE RANGE IS A REF AND NOTHING MORE. It is a way of reading the record, not a
// preference about the app; a window set on a Saturday that outlived the day it
// was chosen on would be a screen quietly answering a different question.
//
// THE MODE IS THE OTHER WAY ROUND and is persisted in `prefs.insightsMode`:
// which of the two records the money half reads is a preference about the app,
// not a way of reading one sitting's numbers.

import { computed, ref, watch } from 'vue';
import { prefs } from '../../core/storage.js';
import { betRecords, findTicket } from '../../store.js';
import { findAny } from './strategies.js';
import { outcomes, rejected, trailToday } from './paper-trail.js';
import { betRow, sportCode } from '../history-view.js';
import { TIER_LABEL } from '../format.js';
import { CLASSIC } from '../engine/strategies/classic.js';
import { canonicalStrategyId } from '../engine/strategies/aliases.js';
import { emptyAgg, insightsModel, keyFor, modeSpec, windowFor } from '../insights.js';
import { paperByKey, paperDays, expandByKey, readRollup } from '../insights-rollup.js';
// The settled refusal log, aggregated. Its own store and its own row: see
// `lib/rejections.js` for why it is never folded in with the proposals.
import { refusedAgg } from '../rejections.js';

const inBrowser = typeof window !== 'undefined';

/** Which window the screen is reading. 30d, which is the one the pad is scored on. */
export const range = ref('30d');

/** Where the mode is kept between opens. `prefs.insightsMode`. */
export const MODE_KEY = 'insightsMode';

/**
 * Which record the money half reads — and UNLIKE the range, this one is
 * remembered.
 *
 * A window is a way of reading the record and it belongs to the sitting; which
 * record is being read is a preference about the app, and a punter who came to
 * this screen for the paper trail should not have to say so every morning.
 * Proposed is the default, here and in `insightsModel`.
 */
export const mode = ref(modeSpec(prefs[MODE_KEY]).value);

watch(mode, (value) => {
  const next = modeSpec(value).value;
  // Through `modeSpec`, so nothing but one of the two words is ever stored: a
  // junk value written here would come back on the next open and be silently
  // treated as Proposed anyway.
  if (next !== value) mode.value = next;
  prefs[MODE_KEY] = next;
});

/** How many weeks the heatmap draws. Ten under 360px, so a cell stays 24px wide. */
export const heatWeeks = ref(12);

/** Re-measure the grid. The page calls this at mount and on a resize. */
export function measureHeat(width = inBrowser ? window.innerWidth : 390) {
  heatWeeks.value = width < 360 ? 10 : 12;
  return heatWeeks.value;
}

/**
 * What printed a ticket, as the pad would name it today. A strategy renamed
 * since shows its new name; one deleted falls back to the label frozen on the
 * multi. Exactly what History does, and for the same reason.
 *
 * A MISSING id AND AN EXPLICIT NULL ARE NOT THE SAME THING. Missing means a
 * ticket written before strategies existed, and Classic is the right guess —
 * `migrate.js` stamps exactly that. Null means a ticket NO strategy printed:
 * `handTicket` sets it, and "Logged by hand" is what the record says it is.
 * Guessing Classic there would file a punter's own bet under a strategy and
 * corrupt that strategy's record with it.
 */
const strategyNameFor = (multi) => (
  multi?.strategyId === null
    ? (multi.label ?? CLASSIC.name)
    : findAny(multi?.strategyId ?? CLASSIC.id)?.name ?? multi?.label ?? CLASSIC.name
);

/**
 * Every placed bet with its frozen ticket and its row.
 *
 * `findTicket` rather than the bet record's own fields, because
 * `bet-record.strategyId` is WRONG on anything written before the strategy
 * engine — the migration stamped them all Classic — and the frozen multi is the
 * only thing left that knows what printed the ticket.
 */
export const betEntries = computed(() => betRecords.value.map((bet) => {
  const ticket = findTicket(bet.multiId);
  return {
    key: bet.id,
    bet,
    ticket,
    legs: ticket?.legs ?? [],
    row: betRow({ bet, ticket, strategyName: strategyNameFor(ticket?.multi) }),
  };
}));

/**
 * Every strategy the app knows a name for, keyed by id — the CANONICAL id,
 * because that is what `keyFor` folds the trail under. A row written under a
 * retired id lands on its heir's entry, which `findAny` names as the pad names
 * it today; the frozen name on the record is only the fallback, for a strategy
 * that has been deleted rather than merged.
 */
export const strategyNames = computed(() => {
  const out = {};
  const add = (rawId, frozen) => {
    const id = canonicalStrategyId(rawId);
    if (id && !out[id]) out[id] = findAny(id)?.name ?? frozen ?? id;
  };
  for (const e of betEntries.value) add(e.row?.strategyId, e.row?.strategy);
  for (const day of Object.values(outcomes.value ?? {})) {
    for (const p of day ?? []) add(p?.strategyId, p?.strategyName);
  }
  return out;
});

// --- the paper trail, folded ---------------------------------------------------
//
// The screen reads `lib/insights-rollup.js` — `paperByKey` over the live trail
// plus the all-time rollup written at settle time. The two folds below are the
// live-trail-only reading the screen shipped with, kept because the tests pin
// the fold's arithmetic against them and because they are the fallback a
// device with no rollup yet gets identical numbers from.

/**
 * `{ strategyKey: Agg }` over the days a window covers.
 *
 * The key is the strategy's id, except Classic, which splits by tier — see
 * `keyFor`. Money counts DECIDED proposals only, the same rule `summariseDay`
 * states; a leg counts toward CLV only when it was measured against a close.
 */
export function paperByKeyShim(w, settledByDate) {
  const byKey = {};
  const at = (key) => {
    if (!byKey[key]) byKey[key] = emptyAgg();
    return byKey[key];
  };

  for (const [date, list] of Object.entries(settledByDate ?? {})) {
    if (w?.from && date < w.from) continue;
    if (w?.to && date > w.to) continue;
    for (const p of list ?? []) {
      const agg = at(keyFor(p?.strategyId, p?.multi?.tier, CLASSIC.id));
      agg.proposed += 1;
      if (p?.outcome && p.outcome !== 'pending') {
        agg.decided += 1;
        if (p.outcome === 'won') agg.won += 1;
        if (p.outcome === 'lost') agg.lost += 1;
        if (p.outcome === 'void') agg.void += 1;
        agg.staked += Number(p.staked) || 0;
        agg.returned += Number(p.returned) || 0;
      }
      for (const leg of p?.legs ?? []) {
        agg.legs += 1;
        // The same test `foldDay` counts by: a leg is evidence about the close
        // once it has been measured against one (engine review #6).
        const closed = !!leg?.clvSource && Number.isFinite(leg?.clv);
        if (closed) {
          agg.legsWithClose += 1;
          agg.clvSum += leg.clv;
          agg.clvSqSum += leg.clv * leg.clv;
        }
        const code = sportCode(leg?.sportTitle ?? leg?.sport);
        if (!code) continue;
        const sport = agg.sports[code] ?? (agg.sports[code] = { legs: 0, won: 0, lost: 0, void: 0, legsWithClose: 0, clvSum: 0 });
        sport.legs += 1;
        if (leg?.result === 'won') sport.won += 1;
        if (leg?.result === 'lost') sport.lost += 1;
        if (leg?.result === 'void') sport.void += 1;
        if (closed) {
          sport.legsWithClose += 1;
          sport.clvSum += leg.clv;
        }
      }
    }
  }
  return byKey;
}

/** How many proposals were DECIDED on each day, for the heatmap's levels. */
export function paperDaysShim(settledByDate) {
  const out = {};
  for (const [date, list] of Object.entries(settledByDate ?? {})) {
    const n = (list ?? []).filter((p) => p?.outcome && p.outcome !== 'pending').length;
    if (n) out[date] = n;
  }
  return out;
}

/**
 * Every `multi.id` the pad ever proposed. The proposed → placed → won funnel is
 * a real join on this — `proposals.js` builds a proposal's multi with the same
 * id a bet later carries as `multiId` — and without the set there is nothing
 * honest to put in the middle band.
 */
export function proposedIdsFrom(settledByDate) {
  const ids = new Set();
  for (const list of Object.values(settledByDate ?? {})) {
    for (const p of list ?? []) if (p?.multi?.id) ids.add(p.multi.id);
  }
  return ids;
}

/** `{ date: { key: Agg } }` → `{ date: decided }`, the heatmap's levels. */
function decidedByDay(days) {
  const out = {};
  for (const [date, byKey] of Object.entries(days ?? {})) {
    let n = 0;
    for (const agg of Object.values(byKey ?? {})) n += Number(agg?.d) || 0;
    if (n) out[date] = n;
  }
  return out;
}

/** The window the range names, on the pad's own clock. */
export const activeWindow = computed(() => windowFor(range.value, trailToday.value));

/**
 * The all-time rollup Slice D writes into `prefs.proposalRollup`. Declared here
 * so the swap marked above is one line rather than a new ref and a new
 * listener; nothing reads it until `paperByKey` lands.
 */
export const rollup = ref(readRollup());

/**
 * Every day the trail can still see, folded: `{ 'YYYY-MM-DD': { key: Agg } }`.
 *
 * ALL TIME rather than the window, and then cut inside `insightsModel`: the
 * heatmap draws twelve weeks whatever the range says, and the paper curve reads
 * the same map through the window. One read of the rollup for both, and one
 * shape — the rollup's own — so the curve and the calendar cannot end up
 * counting a day differently.
 */
export const paperDaysByDate = computed(() =>
  paperDays({ from: null, to: trailToday.value }, { outcomes: outcomes.value, rollup: rollup.value }));

/** The window BEFORE this one, on paper: what the hero's delta chip compares against. */
export const paperPrevByKey = computed(() => {
  const w = activeWindow.value;
  if (!w?.prevFrom) return {};
  return expandByKey(paperByKey({ from: w.prevFrom, to: w.prevTo }, { outcomes: outcomes.value, rollup: rollup.value }));
});

/** The whole screen. One computed, so a range tap re-reads everything at once. */
export const model = computed(() => insightsModel({
  entries: betEntries.value,
  // The window's paper record: live days folded with the all-time rollup Slice
  // D writes at settle time, expanded to the long names this model reads. A
  // live day always goes through the same fold as a rolled-up one, so the two
  // cannot disagree about a strategy's numbers.
  byKey: expandByKey(paperByKey(activeWindow.value, { outcomes: outcomes.value, rollup: rollup.value })),
  byKeyPrev: paperPrevByKey.value,
  // WHAT THE BAR REFUSED over the same window. Its own store, never folded in
  // with the proposals — see `lib/rejections.js` — and counted from the settled
  // refusal log rather than the live one, because an unsettled refusal has no
  // close to be measured against yet.
  refused: refusedAgg(rejected.value, { from: activeWindow.value.from, to: activeWindow.value.to }),
  // The heatmap wants every day it can draw, not the range: decided tickets a
  // day, from the rollup where the live trail has already been pruned.
  paperDays: decidedByDay(paperDaysByDate.value),
  // And the paper curve wants the same days with their money on them, which is
  // the per-day map itself rather than the count taken out of it.
  paperDaysByDate: paperDaysByDate.value,
  proposedIds: proposedIdsFrom(outcomes.value),
  range: range.value,
  mode: mode.value,
  today: trailToday.value,
  names: strategyNames.value,
  tierLabel: TIER_LABEL,
  classicId: CLASSIC.id,
  weeks: heatWeeks.value,
}));

if (inBrowser) {
  // Imported here rather than at the top: core/state.js registers window
  // listeners as it loads, and a module-scope import would drag a browser into
  // the node test runner. Same shape as stores/paper-trail.js.
  //
  // The trail's own store reloads `outcomes` on the same event, and `model`
  // reads that ref — so this exists for the day the rollup arrives and has a
  // ref of its own to refresh.
  import('../../core/state.js')
    .then(({ bus }) => bus.on('settled', () => { rollup.value = readRollup(); }))
    .catch(() => {});
}
